import React, { useState } from "react"
import { Link, useParams } from "react-router-dom"
import dayjs from "dayjs"
import { useQueryClient } from "@tanstack/react-query"
import PropTypes from "prop-types"

//components
import GridTypeA from "components/grids/GridTypeA/GridTypeA"
import TitleSubtitle from "components/ag-grid-cell-renderers/TitleSubtitle/TitleSubtitle"
import { UserBadge } from "components-design-system"
import Modal from "components/Modal/Modal"
import ActionItemActions from "components/ag-grid-cell-renderers/ActionItemActions/ActionItemActions"

// assets
import empty_action_item_icon from "assets/images/empty_action_items.png"

// styles
import "./ActionItemsGrid.scss"

// apis
import { useUserPermissions } from "api/hooks"
import { useDeleteActionItem, useSendReminder } from "api/hooks/actionItems/useActionItemsApi"
import { useUserSettings } from "api/hooks"

// utils
import queryKeys from "api/utils/queryKeys"
import { actionPriorityColDef, dateColDef, formsColDefCsrm, filesColDefCsrm } from "components/grids/columnTemplates"

const ActionItemsGrid = ({
  type,
  secondaryType,
  actionItems = [],
  activeFilter,
  gridRef,
  paginationPageSize,
  paginationPageSizeSelector,
  domLayout,
  sideBar,
  onOpenPanel,
  setSelectedActionItem,
  refetchActionItems,
  canEditAi = true,
}) => {
  const queryClient = useQueryClient()
  const { studyId, activityId, requestId } = useParams()

  const filteredData = actionItems.filter((item) => {
    if (activeFilter === "all") return true
    if (activeFilter === "forms") return item?.forms?.length > 0
    if (activeFilter === "documents") return item?.docs?.length > 0
    return true
  })

  //local state
  const [openDeleteActionItemModal, setOpenDeleteActionItemModal] = useState(false)
  const [itemToDelete, setItemToDelete] = React.useState()
  const [disableReminderSet, setDisabledReminderSet] = useState(new Set())
  const [itemForReminder, setItemForReminder] = useState(null)
  const [openSendReminder, setOpenSendReminder] = useState(false)

  //server state
  const deleteActionItem = useDeleteActionItem(itemToDelete?.id)
  const { data: userSettings } = useUserSettings()
  const { data: permissions } = useUserPermissions()
  const sendReminder = useSendReminder()

  // NOTE: see https://www.ag-grid.com/react-data-grid/filter-set-filter-list/#cell-renderer
  const AssigneeFilterCellRenderer = (props) => {
    const [value, setValue] = React.useState()
    const getInitials = (name) => {
      if (!name) return ""
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
    }

    React.useEffect(() => {
      if (!props.value) {
        setValue(props.isFilterRenderer ? "(Blanks)" : props.value)
      } else if (props.value === "(Select All)") {
        setValue(props.value)
      } else {
        const badge = `<span class="user-badge-container no-text-highlight sm-size">${getInitials(
          props.value.full_name
        )}</span>`
        setValue(`<div style="display:flex; gap: 4px;nalign-items: center;">${badge} ${props.value.full_name}</div>`)
      }
    }, [])

    return <div dangerouslySetInnerHTML={{ __html: value }}></div>
  }

  const onToggleSendReminderModal = (action) => {
    setOpenSendReminder(action ? true : false)
    setItemForReminder(action)
  }

  const onSendReminder = () => {
    sendReminder(
      { id: `${itemForReminder.id}/send-reminder/` },
      {
        onSuccess: (resp) => {
          onToggleSendReminderModal(null)
          disableReminderSet.add(itemForReminder.id)
          setDisabledReminderSet(disableReminderSet)
          refetchActionItems()
        },
      }
    )
  }

  const invalidateActionItems = () => {
    if (type === "activity") {
      queryClient.invalidateQueries({
        queryKey: queryKeys.actionItems.activity(Number(studyId), Number(activityId)),
      })
    } else if (type === "request") {
      queryClient.invalidateQueries({
        queryKey: queryKeys.requests.actionItems(Number(requestId)),
      })
    }
  }

  const onDeleteActionItem = () => {
    deleteActionItem(
      {},
      {
        onSuccess: () => {
          invalidateActionItems()
          setOpenDeleteActionItemModal(false)
          setItemToDelete(null)
        },
      }
    )
  }

  const getColumnDefs = (type) => {
    const colDefs = [
      {
        ...actionPriorityColDef,
      },
      {
        headerName: "Name/Task type",
        field: "title",
        cellRenderer: TitleSubtitle,
        cellRendererParams: {
          title: "title",
          subtitle: "task_type_state",
        },
        pinned: "left",
        comparator: (valueA, valueB) => {
          return valueA?.localeCompare(valueB, undefined, { sensitivity: 'base' }) || 0;
        },
      },
      {
        headerName: "Step",
        field: "associated_wf_state",
      },
      {
        headerName: "Status",
        field: "status",
        cellRenderer: "ActionItemStatus",
        getValue: (params) => {
          let { data } = params
          let value = data.status
          if (data.is_cancelled) value = "Cancelled"
          else if (data.is_complete) value = "Completed"
          return `${value}`
        },
        cellClass: ["col-badge"],
      },
      {
        headerName: "Due by",
        field: "due_date",
        ...dateColDef,
      },
      {
        headerName: "Assignees",
        field: "assigned_to_users",
        cellRenderer: ({ data }) => {
          return (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {data.assigned_to_users.map((user) => (
                <UserBadge
                  key={user.id}
                  user={user.full_name}
                />
              ))}
            </div>
          )
        },
        filter: "agSetColumnFilter",
        filterParams: {
          keyCreator: ({ value }) => value.id,
          valueFormatter: ({ value }) => value?.full_name || "(Blanks)",
          cellRenderer: AssigneeFilterCellRenderer,
        },
        cellClass: ["col-badge"],
      },
    ]

    if (type !== "request" && type !== "my-prime") {
      colDefs.push(formsColDefCsrm())
    }

    if (type !== "my-prime") {
      colDefs.push(filesColDefCsrm())
    }

    if (type === "my-prime") {
      if (secondaryType === "requests") {
        colDefs.push(
          {
            headerName: "Request",
            field: "request_id",
            cellClass: "action-pill",
            cellRenderer: (params) => {
              if (params.data.request_id)
                return (
                  <div className="study-pill">
                    <Link
                      className="study-pill-text"
                      target="_blank"
                      to={`/request/${params.data.request_uniqid}/`}
                    >
                      {params.value}
                    </Link>
                  </div>
                )
            },
            suppressSizeToFit: false,
          },
          {
            headerName: "Type",
            field: "request_type",
          },
          {
            headerName: "Request Due Date",
            field: "request_due_date",
            ...dateColDef,
          }
        )
      } else {
        colDefs.push(
          {
            headerName: "Compliance Due Date",
            field: "compliance_due_date",
            ...dateColDef,
          },
          {
            headerName: "Activity Due Date",
            field: "activity_due_date",
            ...dateColDef,
          },
          {
            headerName: "Study",
            field: "study_id",
            cellClass: "action-pill",
            cellRenderer: (params) => {
              if (params.data.study_uniqid)
                return (
                  <div className="study-pill">
                    <Link
                      className="study-pill-text"
                      target="_blank"
                      to={`/study/${params.data.study_uniqid}/overview`}
                    >
                      {params.value}
                    </Link>
                  </div>
                )
            },
            suppressSizeToFit: false,
          },
          {
            headerName: "Activity",
            field: "activity_title",
            filter: "agSetColumnFilter",
            cellRenderer: (props) => {
              let title = props.data.activity_title
              let uniqid = props.data.activity_uniqid
              return (
                <div>
                  {uniqid ? (
                    <Link
                      target="_blank"
                      to={`/study/${props.data.study_uniqid}/activity/${uniqid}`}
                    >
                      {title}
                    </Link>
                  ) : (
                    title
                  )}
                </div>
              )
            },
            suppressSizeToFit: false,
            cellRendererParams: {
              title: "activity_title",
              subtitle: "activity_uniqid",
            },
            cellStyle: { textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden", fontSize: "13px" },
          }
        )
      }
    }

    if (canEditAi !== false) {
      colDefs.push({
        headerName: "",
        field: "actions",
        cellRenderer: ActionItemActions,
        cellRendererParams: {
          type,
          permissions,
          onOpenPanel,
          setOpenDeleteActionItemModal,
          setSelectedActionItem,
          setItemToDelete,
          onToggleSendReminderModal,
          disableReminderSet,
          itemForReminder,
        },
        suppressMenu: true,
        sortable: false,
        resizable: false,
        cellClass: ["col-badge"],
        pinned: "right",
        width: 90,
      })
    }

    return colDefs
  }

  const columnDefs = getColumnDefs(type)

  const gridOptions = {
    defaultColDef: {
      enablePivot: true,
      enableCharts: false,
      enableRowGroup: false,
    },
    columnTypes: {
      date: {
        filter: "agDateColumnFilter",
        filterParams: {
          resetButton: true,
          comparator: (filterLocalDateAtMidnight, cellValue) => {
            let cellDate = null
            let dateAsString = ""
            dateAsString = cellValue
            if (dateAsString === null) {
              return 0
            }
            cellDate = dayjs(dateAsString).toDate()
            if (cellDate < filterLocalDateAtMidnight) {
              return -1
            } else if (cellDate > filterLocalDateAtMidnight) {
              return 1
            } else {
              return 0
            }
          },
        },
      },
    },
    initialSortModel: [{ colId: "due_date", sort: "desc" }],
  }

  const renderEmptyGrid = (
    <div className="empty-action-items-container">
      <img
        className="empty_action_item_icon"
        src={empty_action_item_icon}
        alt="empty action items"
      />
      <p className="empty-action-items-label">You don't have any action items</p>
    </div>
  )

  return (
    <>
      <div className="action-items-widget-grid">
        {actionItems.length > 0 ? (
          <GridTypeA
            className="action-items-grid"
            gridRef={gridRef}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            rowData={filteredData}
            pagination={true}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={paginationPageSizeSelector}
            showRowCount={false}
            domLayout={domLayout}
            sideBar={sideBar}
            autoSizeStrategy="fitGridWidth"
          />
        ) : (
          renderEmptyGrid
        )}
      </div>
      <Modal
        title={`Delete "${itemToDelete?.title}" action item`}
        message={"This action can't be undone."}
        type="delete"
        onOk={onDeleteActionItem}
        onCancel={() => {
          setItemToDelete(null)
          setOpenDeleteActionItemModal(false)
        }}
        open={openDeleteActionItemModal}
      />
      <Modal
        title={`Send an E-mail reminder to ${itemForReminder?.assigned_to_users
          ?.map((user) => user.full_name)
          .join(", ")}?`}
        message={`Action Item: ${itemForReminder?.title}`}
        type="bell"
        onOk={onSendReminder}
        okText={"Send"}
        onCancel={() => onToggleSendReminderModal(null)}
        open={openSendReminder}
      />
    </>
  )
}

ActionItemsGrid.propTypes = {
  type: PropTypes.oneOf(["activity", "request", "my-prime"]).isRequired,
  actionItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeFilter: PropTypes.oneOf(["all", "forms", "documents"]).isRequired,
  gridRef: PropTypes.object,
  paginationPageSize: PropTypes.number,
  paginationPageSizeSelector: PropTypes.arrayOf(PropTypes.number),
  domLayout: PropTypes.string,
  sideBar: PropTypes.bool,
  onOpenPanel: PropTypes.func,
  setSelectedActionItem: PropTypes.func.isRequired,
  refetchActionItems: PropTypes.func.isRequired,
}

export default ActionItemsGrid
